import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { theme } from '../constants/theme';

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.colors.background,
    fontSize: '14px',
    '& fieldset': {
      borderColor: theme.colors.border,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.primary,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '12px 14px',
  },
  '& .MuiInputLabel-root': {
    color: theme.colors.text,
    fontSize: '14px',
    '&.Mui-focused': {
      color: theme.colors.primary,
    },
  },
}));

const StyledFormControl = styled(FormControl)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.colors.background,
    fontSize: '14px',
    '& fieldset': {
      borderColor: theme.colors.border,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.primary,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '12px 14px',
  },
  '& .MuiSelect-select': {
    fontSize: '14px',
    padding: '12px 14px',
  },
  '& .MuiInputLabel-root': {
    color: theme.colors.text,
    fontSize: '14px',
    '&.Mui-focused': {
      color: theme.colors.primary,
    },
  },
  '& .MuiMenuItem-root': {
    fontSize: '14px',
  },
}));

const StyledButton = styled(Button)(() => ({
  backgroundColor: theme.colors.primary,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 20px',
  height: '40px',
  fontSize: '14px',
  '&:hover': {
    backgroundColor: '#c73e24',
  },
}));

const DeleteButton = styled(Button)(() => ({
  borderColor: theme.colors.error,
  color: theme.colors.error,
  textTransform: 'none',
  fontWeight: 600,
  padding: '8px 20px',
  height: '40px',
  fontSize: '14px',
  '&:hover': {
    borderColor: theme.colors.error,
    backgroundColor: 'rgba(239, 68, 68, 0.04)',
  },
}));

const ManageProjectsModal = ({ 
  isOpen, 
  onClose, 
  projects,
  onSaveProject,
  onDeleteProject,
  language = 'nl'
}) => {
  const [newProject, setNewProject] = useState('');
  const [selectedProjectToDelete, setSelectedProjectToDelete] = useState('');

  useEffect(() => {
    if (!isOpen) {
      // Reset form when dialog closes
      setNewProject('');
      setSelectedProjectToDelete('');
    }
  }, [isOpen]);

  const normalizeInput = (value) => {
    return value.toLowerCase().replace(/[^0-9a-z_]+/g, '_').replace(/^_+|_+$/g, '');
  };

  const handleSaveProject = (e) => {
    e.preventDefault();
    if (newProject.trim()) {
      onSaveProject(normalizeInput(newProject));
      setNewProject('');
      onClose();
    }
  };

  const handleDeleteProject = (e) => {
    e.preventDefault();
    if (selectedProjectToDelete) {
      onDeleteProject(selectedProjectToDelete);
      setSelectedProjectToDelete('');
      onClose();
    }
  };

  const text = {
    nl: {
      title: 'Beheer Projecten',
      newProject: 'Nieuw project',
      projectName: 'Project naam',
      add: 'Toevoegen',
      deleteProject: 'Verwijder project',
      selectProject: 'Selecteer project',
      select: 'Selecteer',
      delete: 'Verwijderen'
    },
    en: {
      title: 'Manage Projects',
      newProject: 'New project',
      projectName: 'Project name',
      add: 'Add',
      deleteProject: 'Delete project',
      selectProject: 'Select project',
      select: 'Select',
      delete: 'Delete'
    }
  };

  const t = text[language] || text.nl;

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: `${theme.borderRadius.lg}px`,
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        pb: 2
      }}>
        {t.title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.colors.textSecondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ paddingTop: '32px !important', paddingBottom: '32px !important' }}>
        {/* Add Project Form */}
        <Box component="form" onSubmit={handleSaveProject} sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AddIcon sx={{ color: theme.colors.primary, mr: 1 }} />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: theme.colors.text }}>
              {t.newProject}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <StyledTextField
              fullWidth
              label={t.projectName}
              value={newProject}
              onChange={(e) => setNewProject(e.target.value)}
              inputProps={{ maxLength: 100 }}
              size="small"
              autoFocus
            />
            <StyledButton type="submit" sx={{ minWidth: '120px' }}>
              {t.add}
            </StyledButton>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Delete Project Form */}
        <Box component="form" onSubmit={handleDeleteProject}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DeleteOutlineIcon sx={{ color: theme.colors.error, mr: 1 }} />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: theme.colors.text }}>
              {t.deleteProject}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <StyledFormControl fullWidth size="small">
              <InputLabel>{t.selectProject}</InputLabel>
              <Select
                value={selectedProjectToDelete}
                label={t.selectProject}
                onChange={(e) => setSelectedProjectToDelete(e.target.value)}
              >
                <MenuItem value="">{t.select}</MenuItem>
                {projects.map((proj) => (
                  <MenuItem key={proj} value={proj}>
                    {proj}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
            <DeleteButton 
              type="submit" 
              variant="outlined"
              sx={{ minWidth: '120px' }}
              disabled={!selectedProjectToDelete}
            >
              {t.delete}
            </DeleteButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ManageProjectsModal;

